'use client'

import { cn } from '@/lib/utils'

type HighlightLanguage = 'json' | 'bash' | 'javascript' | 'python' | 'http'

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function highlightJson(code: string): string {
  return escapeHtml(code).replace(
    /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g,
    (match, quoted, colon) => {
      if (quoted && colon) {
        return `<span class="text-brand-purple">${quoted}</span>${colon}`
      }
      if (quoted) {
        return `<span class="text-emerald-600">${quoted}</span>`
      }
      if (/true|false|null/.test(match)) {
        return `<span class="text-amber-600">${match}</span>`
      }
      return `<span class="text-brand-cornflower">${match}</span>`
    }
  )
}

function highlightBash(code: string): string {
  return escapeHtml(code)
    .replace(/(-[A-Za-z]+|--[\w-]+)/g, '<span class="text-brand-cornflower">$1</span>')
    .replace(/("(?:\\.|[^"\\])*")/g, '<span class="text-emerald-600">$1</span>')
    .replace(/\b(curl|POST|GET|DELETE|Authorization|Bearer|Content-Type)\b/g, '<span class="text-brand-purple">$1</span>')
}

function highlightJs(code: string): string {
  return escapeHtml(code)
    .replace(/(\/\/.*$)/gm, '<span class="text-brand-muted/80">$1</span>')
    .replace(/("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`)/g, '<span class="text-emerald-600">$1</span>')
    .replace(/\b(const|let|await|async|import|from|return|new|fetch)\b/g, '<span class="text-brand-purple">$1</span>')
}

function highlightPython(code: string): string {
  return escapeHtml(code)
    .replace(/(#.*$)/gm, '<span class="text-brand-muted/80">$1</span>')
    .replace(/("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')/g, '<span class="text-emerald-600">$1</span>')
    .replace(/\b(import|from|with|as|def|return|requests)\b/g, '<span class="text-brand-purple">$1</span>')
}

function highlightHttp(code: string): string {
  return escapeHtml(code)
    .replace(/\b(Authorization|Bearer|Content-Type|Accept):/g, '<span class="text-brand-purple">$1</span>:')
    .replace(/(Bearer\s+\S+)/g, '<span class="text-emerald-600">$1</span>')
}

export function highlightCode(code: string, language: HighlightLanguage): string {
  switch (language) {
    case 'json':
      return highlightJson(code)
    case 'bash':
      return highlightBash(code)
    case 'javascript':
      return highlightJs(code)
    case 'python':
      return highlightPython(code)
    case 'http':
      return highlightHttp(code)
    default:
      return escapeHtml(code)
  }
}

interface SyntaxPreProps {
  code: string
  language: HighlightLanguage
  className?: string
}

export const CODE_BOX_CLASS =
  'rounded-xl bg-brand-cornflower/20 p-4 ring-1 ring-brand-cornflower/30 font-mono text-xs leading-relaxed text-brand-navy'

export function SyntaxPre({ code, language, className }: SyntaxPreProps) {
  return (
    <pre
      className={cn('overflow-x-auto', CODE_BOX_CLASS, className)}
    >
      <code dangerouslySetInnerHTML={{ __html: highlightCode(code, language) }} />
    </pre>
  )
}
